import { useCallback, useEffect, useMemo } from 'react';
import { history, useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { selectValByCondition } from '@home/utils/common';
import {
  STUDIO_HUB_SHELL,
  isStudioSidebarGameHubSegment,
} from '../constant';
import type { StudioHubShellKind } from '../constant';
import type { IDashboardItem } from '../type';

export interface IUseStudioHubTabSwitchParams {
  studioDashboardList: IDashboardItem[];
  hubBasePath: string;
  hubShellKind: StudioHubShellKind;
  pathname: string;
  studioId?: string;
}

export interface IUseStudioHubTabSwitchResult {
  hubSubTabs: IDashboardItem[];
  showTabs: boolean;
  tabItems: Array<{ id: string; label: string; }>;
  activeTabId: string;
  onTabChange: (activeTab: { id: string; }) => void;
  homeReqParams: string;
}

/**
 * Studio Wrap 内 Tea Tabs：与 studio_dashboard_list 子 path 对齐的切换逻辑。
 * Home 工作台使用；侧栏类枢纽默认不展示（URL 第二段为 gameCode，与子 Tab 语义冲突）。
 */
export function useStudioHubTabSwitch(
  params: IUseStudioHubTabSwitchParams,
): IUseStudioHubTabSwitchResult {
  const {
    studioDashboardList,
    hubBasePath,
    hubShellKind,
    pathname,
    studioId,
  } = params;
  const { locale } = useIntl();

  const getTabSegment = useCallback(
    (path: string): string => {
      const prefix = `${hubBasePath}/`;
      return path.startsWith(prefix) ? path.slice(prefix.length) : '';
    },
    [hubBasePath],
  );

  const hubSubTabs = useMemo<IDashboardItem[]>(() => {
    return studioDashboardList.filter(
      (item: IDashboardItem) =>
        Boolean(item.path) &&
        item.path !== hubBasePath &&
        item.path!.startsWith(`${hubBasePath}/`),
    );
  }, [studioDashboardList, hubBasePath]);

  const tab = useMemo(() => {
    if (hubShellKind !== STUDIO_HUB_SHELL.HOME) {
      return undefined;
    }
    const match = pathname.match(/\/studio\/[^/]+\/home\/([^/]+)/);
    return match ? match[1] : undefined;
  }, [pathname, hubShellKind]);

  const showTabs =
    hubSubTabs.length > 1 && !isStudioSidebarGameHubSegment(hubShellKind);

  const hubSubTabSegmentForReq = useMemo(() => {
    if (hubShellKind === STUDIO_HUB_SHELL.HOME) {
      return tab;
    }
    return undefined;
  }, [hubShellKind, tab]);

  const homeReqParams = useMemo<string>(() => {
    if (isStudioSidebarGameHubSegment(hubShellKind)) {
      return hubBasePath;
    }
    if (hubSubTabs.length === 0) {
      return hubBasePath;
    }
    if (hubSubTabs.length === 1) {
      return hubSubTabs[0].path;
    }
    const matched = hubSubTabs.find(
      (item) => getTabSegment(item.path) === hubSubTabSegmentForReq,
    );
    return matched ? matched.path : hubSubTabs[0].path;
  }, [
    hubShellKind,
    hubBasePath,
    hubSubTabs,
    hubSubTabSegmentForReq,
    getTabSegment,
  ]);

  const activeTabId = useMemo<string>(() => {
    if (hubSubTabs.length <= 1) {
      return '';
    }
    const matched = hubSubTabs.find(
      (item) => getTabSegment(item.path) === hubSubTabSegmentForReq,
    );
    return matched
      ? getTabSegment(matched.path)
      : getTabSegment(hubSubTabs[0].path);
  }, [hubSubTabs, hubSubTabSegmentForReq, getTabSegment]);

  const tabItems = useMemo(() => {
    return hubSubTabs.map(item => ({
      id: getTabSegment(item.path),
      label: selectValByCondition(
        locale === LANGUAGE_EN,
        item.name_en,
        item.name_cn,
      ),
    }));
  }, [hubSubTabs, locale, getTabSegment]);

  const onTabChange = useCallback(
    (activeTab: { id: string; }) => {
      history.replace(`/studio/${studioId}/${hubShellKind}/${activeTab.id}`);
    },
    [studioId, hubShellKind],
  );

  useEffect(() => {
    if (studioId === 'casual' || hubSubTabs.length <= 1) {
      return;
    }
    if (hubShellKind !== STUDIO_HUB_SHELL.HOME) {
      return;
    }
    if (!tab) {
      const firstSegment = getTabSegment(hubSubTabs[0].path);
      history.replace(`/studio/${studioId}/home/${firstSegment}`);
    }
  }, [hubSubTabs, tab, studioId, hubShellKind, getTabSegment]);

  return {
    hubSubTabs,
    showTabs,
    tabItems,
    activeTabId,
    onTabChange,
    homeReqParams,
  };
}
